const toHex = (buffer) => {
  return Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
};

const hashValue = async (value) => {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  const encoder = new TextEncoder();
  const data = encoder.encode(normalized);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return toHex(hashBuffer);
};

const getEnv = (name, fallback) => {
  return process.env[name] || fallback;
};

export const FB_API_VERSION = getEnv('REACT_APP_FB_API_VERSION', 'v17.0');
export const FB_PIXEL_ID = getEnv('REACT_APP_FB_PIXEL_ID', '<PIXEL_ID>');
export const FB_ACCESS_TOKEN = getEnv('REACT_APP_FB_CONVERSIONS_ACCESS_TOKEN', '<ACCESS_TOKEN>');

export const buildUserData = async ({
  email,
  phone,
  firstName,
  lastName,
  city,
  state,
  zip,
  country,
  gender,
  dateOfBirth,
  clientUserAgent,
  fbp,
  fbc,
}) => {
  const [em, ph, fn, ln, ct, st, zp, cn, gd, dob] = await Promise.all([
    hashValue(email),
    hashValue(phone),
    hashValue(firstName),
    hashValue(lastName),
    hashValue(city),
    hashValue(state),
    hashValue(zip),
    hashValue(country),
    hashValue(gender),
    hashValue(dateOfBirth),
  ]);

  const userData = {};
  if (em) userData.em = [em];
  if (ph) userData.ph = [ph];
  if (fn) userData.fn = [fn];
  if (ln) userData.ln = [ln];
  if (ct) userData.ct = [ct];
  if (st) userData.st = [st];
  if (zp) userData.zp = [zp];
  if (cn) userData.country = [cn];
  if (gd) userData.ge = [gd];
  if (dob) userData.db = [dob];
  if (clientUserAgent) userData.client_user_agent = clientUserAgent;
  if (fbp) userData.fbp = fbp;
  if (fbc) userData.fbc = fbc;
  return userData;
};

export const buildEventPayload = ({
  eventName,
  eventSourceUrl,
  actionSource = 'website',
  eventId,
  eventTime,
  userData,
  customData,
  attributionData,
  originalEventData,
  optOut,
  status,
}) => {
  const event = {
    event_name: eventName,
    event_time: eventTime || Math.floor(Date.now() / 1000),
    event_source_url: eventSourceUrl,
    action_source: actionSource,
    user_data: userData || {},
  };

  if (eventId) event.event_id = eventId;
  if (optOut !== undefined) event.opt_out = optOut;
  if (status !== undefined) event.status = status;
  if (customData) event.custom_data = customData;
  if (attributionData) event.attribution_data = attributionData;
  if (originalEventData) event.original_event_data = originalEventData;

  return { data: [event] };
};

export const buildContactEvent = async ({
  name,
  email,
  phone,
  pageUrl,
  userAgent,
  fbp,
  fbc,
  message,
}) => {
  const [firstName, lastName] = name ? name.split(' ', 2) : ['',''];
  const userData = await buildUserData({
    email,
    phone,
    firstName,
    lastName,
    clientUserAgent: userAgent,
    fbp,
    fbc,
  });

  return buildEventPayload({
    eventName: 'Contact',
    eventSourceUrl: pageUrl,
    actionSource: 'website',
    userData,
    originalEventData: {
      event_name: 'Contact',
      event_time: Math.floor(Date.now() / 1000),
    },
    customData: {
      content_name: message ? message.slice(0, 200) : undefined,
    },
  });
};

export const sendFacebookServerEvent = async (payload) => {
  const url = `/send-event`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Server Conversions API request failed: ${response.status} ${response.statusText} ${errorText}`);
  }

  return await response.json();
};

export const buildSearchEvent = async ({
  pageUrl,
  userAgent,
  queryText,
  fbp,
  fbc,
}) => {
  const userData = await buildUserData({
    clientUserAgent: userAgent,
    fbp,
    fbc,
  });

  return buildEventPayload({
    eventName: 'Search',
    eventSourceUrl: pageUrl,
    actionSource: 'website',
    userData,
    customData: {
      search_string: queryText,
    },
  });
};

export const buildFindLocationEvent = async ({
  pageUrl,
  userAgent,
  locationName,
  fbp,
  fbc,
}) => {
  const userData = await buildUserData({
    clientUserAgent: userAgent,
    fbp,
    fbc,
  });

  return buildEventPayload({
    eventName: 'FindLocation',
    eventSourceUrl: pageUrl,
    actionSource: 'website',
    userData,
    customData: {
      content_name: locationName,
    },
  });
};
